import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@mui/material';

import { clamp } from './TimelineSlider.utils';
import type { TimelineTick } from './TimelineSlider.utils';

import './TimelineSlider.css';

/**
 * A discrete slider that renders one cell per integer position in [min, max].
 *
 * Replaces the old <input type="range">, which could not carry decorations: a range input paints
 * its track in track coordinates but places values in a thumb-inset coordinate space, so any tick
 * or icon overlay drifts from the thumb by up to half a thumb width. That is also why the old
 * grey/red split had to be hand-tuned per `max` (.efas-max-21 .. .efas-max-29) and was simply
 * missing for every other `max`. Here the split is derived from min/max and is exact for any range.
 *
 * Commits only on pointer release / key press, never mid-drag: every commit fires a WMS request
 * per active layer.
 */
export default memo(function TimelineSlider(props: {
    id?: string,
    /** id of the element that labels this slider - a <div> cannot be a htmlFor target. */
    ariaLabelledBy?: string,
    min: number,
    max: number,
    /** Committed value, owned by the parent. Rendered whenever a drag is not in progress. */
    value: number,
    /** Keyboard increment. Defaults to 1. */
    step?: number,
    /** Blocks input only - `value` still renders, so programmatic playback stays visible. */
    disabled?: boolean,
    ticks?: TimelineTick[],
    /**
     * Defaults to true. Set false when the selection lives outside [min, max] - e.g. on one of the
     * timeline's additional-step buttons, which carry their own pressed state. Input is unaffected:
     * clicking or arrowing the track takes the selection back.
     */
    showThumb?: boolean,
    /** Called for the hovered index only. A tick's own `tooltip` wins over this. */
    renderTooltip?: (index: number) => React.ReactNode,
    /** Screen-reader text for the current index (aria-valuetext). */
    ariaValueText?: (index: number) => string,
    /** Fired once per interaction, on pointer release or key press. Suppressed if value is unchanged. */
    onCommit: (value: number) => void,
}) {
    const trackRef = useRef<HTMLDivElement>(null);
    // null is the sentinel for "not dragging" - 0 and -12 are both legal values, so never
    // test this for truthiness.
    const [drag, setDrag] = useState<number | null>(null);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const step = props.step && props.step > 0 ? props.step : 1;
    const cellCount = props.max - props.min + 1;
    const isEmpty = cellCount <= 0;

    // A live drag always wins over the incoming value, so the parent's reset effects can never
    // fight the user's finger. Clamping also guards Timeline's `val *= 12` path, which can push
    // currentStep well outside [min, max].
    const shown = drag ?? clamp(props.value, props.min, props.max);

    const tickByIndex = useMemo(() => {
        const map = new Map<number, TimelineTick>();
        for (const tick of props.ticks || []) {
            if (tick.index >= props.min && tick.index <= props.max) map.set(tick.index, tick);
        }
        return map;
    }, [props.ticks, props.min, props.max]);

    // Memoised so a drag re-renders the thumb only, not all N cells.
    const cells = useMemo(() => {
        if (isEmpty) return null;
        return Array.from({ length: cellCount }, (_, offset) => {
            const index = props.min + offset;
            const tick = tickByIndex.get(index);
            return <div key={index}
                className={`efas-timeline-cell ${tick?.className || ''}`}
                data-index={index}
                data-region={index < 0 ? 'past' : 'forecast'}
                data-divider={tick?.divider || undefined}>
                {tick?.icon && <span className='efas-timeline-icon'><FontAwesomeIcon icon={tick.icon} /></span>}
            </div>;
        });
    }, [isEmpty, cellCount, props.min, tickByIndex]);

    // Floor, not round: cells are regions, not points. Rounding (what a native range does, because
    // its values are points) would make a click near a cell's left edge jump to the previous cell.
    const indexFromClientX = useCallback((clientX: number) => {
        const rect = trackRef.current?.getBoundingClientRect();
        // width can be 0 when the widget is hidden - never divide by zero.
        if (!rect || rect.width === 0) return clamp(props.value, props.min, props.max);
        const raw = ((clientX - rect.left) / rect.width) * cellCount;
        return clamp(props.min + Math.floor(raw), props.min, props.max);
    }, [props.value, props.min, props.max, cellCount]);

    // Pointer events rather than mouse events: React attaches touchmove as a passive listener, so
    // preventDefault() in an onTouchMove is a no-op and the page scrolls out from under the drag.
    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (props.disabled || isEmpty || e.button !== 0 || !e.isPrimary) return;
        e.preventDefault();                                 // no text selection, no compat mouse events
        e.currentTarget.setPointerCapture?.(e.pointerId);   // keeps working outside the element/window
        trackRef.current?.focus();
        setDrag(indexFromClientX(e.clientX));
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (props.disabled || isEmpty) return;
        const index = indexFromClientX(e.clientX);
        setHoverIndex(index);
        if (drag === null || index === drag) return;        // hovering only - no commit here
        setDrag(index);
    };

    const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
        if (drag === null) return;
        const index = drag;
        setDrag(null);
        // pointerup commits; pointercancel (OS gesture, back-swipe) discards.
        if (e.type === 'pointerup' && index !== props.value) props.onCommit(index);
    };

    const onPointerLeave = () => {
        if (drag === null) setHoverIndex(null);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // e.repeat: a held arrow would otherwise fire a WMS round per repeat.
        if (props.disabled || isEmpty || e.repeat) return;
        const page = Math.max(step, Math.round(cellCount / 10));
        let next: number;
        switch (e.key) {
            case 'ArrowRight': case 'ArrowUp': next = shown + step; break;
            case 'ArrowLeft': case 'ArrowDown': next = shown - step; break;
            case 'PageUp': next = shown + page; break;
            case 'PageDown': next = shown - page; break;
            case 'Home': next = props.min; break;
            case 'End': next = props.max; break;
            default: return;
        }
        e.preventDefault();                                 // arrows and PageUp/Down scroll the page
        next = clamp(next, props.min, props.max);
        if (next !== props.value) props.onCommit(next);
    };

    // A keyboard user can start a drag and then Tab away, or preloading can begin mid-drag.
    useEffect(() => {
        if (props.disabled) {
            setDrag(null);
            setHoverIndex(null);
        }
    }, [props.disabled]);

    // One Tooltip for the whole track, anchored to a virtual element covering the hovered cell -
    // rather than N Tooltip instances, one per cell.
    const hoverAnchor = useMemo(() => hoverIndex === null ? undefined : ({
        getBoundingClientRect: () => {
            const rect = trackRef.current?.getBoundingClientRect();
            if (!rect) return new DOMRect();
            const cellWidth = rect.width / cellCount;
            return new DOMRect(rect.left + (hoverIndex - props.min) * cellWidth, rect.top, cellWidth, rect.height);
        },
    }), [hoverIndex, cellCount, props.min]);

    const tooltipContent = hoverIndex === null ? ''
        : (tickByIndex.get(hoverIndex)?.tooltip ?? props.renderTooltip?.(hoverIndex) ?? '');

    const trackStyle = {
        gridTemplateColumns: `repeat(${Math.max(cellCount, 1)}, 1fr)`,
        // The grey/red boundary, derived from the data instead of hand-tuned per `max`.
        '--efas-timeline-split': `${isEmpty ? 0 : (clamp(-props.min, 0, cellCount) / cellCount) * 100}%`,
    } as React.CSSProperties;

    // The cell centre. The CSS clamps this half a thumb width in from each end, so the thumb never
    // overhangs the track.
    const thumbStyle = {
        '--efas-timeline-thumb-left': `${((shown - props.min + 0.5) / cellCount) * 100}%`,
    } as React.CSSProperties;

    return <Tooltip title={tooltipContent} placement='top' arrow enterDelay={200} leaveDelay={150}
        slotProps={{ popper: { anchorEl: hoverAnchor as never } }}>
        <div ref={trackRef}
            id={props.id}
            className='efas-timeline-slider'
            role='slider'
            tabIndex={props.disabled || isEmpty ? -1 : 0}
            aria-orientation='horizontal'
            aria-labelledby={props.ariaLabelledBy}
            aria-valuemin={props.min}
            aria-valuemax={props.max}
            // Tracks the drag preview, not the committed value, or AT announces stale positions.
            aria-valuenow={isEmpty ? undefined : shown}
            // Mandatory here: aria-valuenow is a step index and is meaningless spoken aloud.
            aria-valuetext={isEmpty ? undefined : props.ariaValueText?.(shown)}
            aria-disabled={props.disabled || undefined}
            style={trackStyle}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            onPointerLeave={onPointerLeave}
            onKeyDown={onKeyDown}>
            {cells}
            {!isEmpty && props.showThumb !== false && <div className='efas-timeline-thumb' style={thumbStyle} />}
        </div>
    </Tooltip>;
})
